/**
 * Web-based market pricing search for Saudi government tenders.
 *
 * Uses Tavily Search API (https://tavily.com) when TAVILY_API_KEY is
 * configured. Gracefully degrades to "not configured" with a clear
 * warning so the rest of the pricing pipeline still works (internal
 * proposals + AI inference are still used).
 */

export interface MarketSearchSource {
  title: string;
  url: string;
  snippet: string;
  publishedAt?: string;
}

export interface MarketSearchResult {
  query: string;
  answer?: string;
  sources: MarketSearchSource[];
  configured: boolean;
  warning?: string;
}

/**
 * Build a Saudi-market focused query. We bias toward transparent sources
 * (Etimad, MOF, government open-data portals) so the AI gets grounded
 * pricing references.
 */
export function buildPricingQueries(args: {
  tenderTitle: string;
  language: "ar" | "en";
  category?: string | null;
}): string[] {
  const { tenderTitle, language, category } = args;
  const title = tenderTitle.slice(0, 120);
  const cat = (category ?? "").slice(0, 60);
  if (language === "ar") {
    return [
      `أسعار مرجعية مناقصة "${title}" السوق السعودي ${cat}`.trim(),
      `منصة اعتماد ترسية ${cat || title} متوسط سعر`.trim(),
      `${cat || title} بيانات مفتوحة حكومية السعودية تكلفة`.trim(),
    ];
  }
  return [
    `Saudi government tender pricing benchmarks for "${title}" ${cat}`.trim(),
    `Etimad awarded contracts ${cat || title} average price Saudi Arabia`.trim(),
    `${cat || title} Saudi open data government cost benchmark`.trim(),
  ];
}

async function tavilySearchOne(
  query: string,
  apiKey: string,
): Promise<MarketSearchResult> {
  try {
    const res = await fetch("https://api.tavily.com/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        api_key: apiKey,
        query,
        search_depth: "advanced",
        include_answer: true,
        max_results: 6,
      }),
    });
    if (!res.ok) {
      return {
        query, sources: [], configured: true,
        warning: `Tavily HTTP ${res.status}`,
      };
    }
    const data = (await res.json()) as any;
    const sources: MarketSearchSource[] = (data?.results ?? [])
      .slice(0, 6)
      .map((r: any) => ({
        title: String(r?.title ?? "").slice(0, 200),
        url: String(r?.url ?? ""),
        snippet: String(r?.content ?? r?.snippet ?? "").slice(0, 600),
        publishedAt: r?.published_date,
      }))
      .filter((s: MarketSearchSource) => s.url);
    return {
      query,
      answer: typeof data?.answer === "string" ? data.answer : undefined,
      sources,
      configured: true,
    };
  } catch (err: any) {
    return {
      query, sources: [], configured: true,
      warning: `Tavily error: ${String(err?.message ?? err).slice(0, 200)}`,
    };
  }
}

/**
 * Run multiple Saudi-market queries in parallel and return aggregated
 * results. If TAVILY_API_KEY is missing, returns a single "not configured"
 * stub so the caller can show a clear UI warning.
 */
export async function searchMarketPrices(args: {
  tenderTitle: string;
  language: "ar" | "en";
  category?: string | null;
}): Promise<MarketSearchResult[]> {
  const apiKey = process.env.TAVILY_API_KEY;
  const queries = buildPricingQueries(args);
  if (!apiKey) {
    return [
      {
        query: queries[0],
        sources: [],
        configured: false,
        warning:
          args.language === "ar"
            ? "البحث الويبي للأسعار غير مُفعَّل (TAVILY_API_KEY غير مضبوط) — يتم الاكتفاء بالعروض الداخلية."
            : "Web pricing search not configured (TAVILY_API_KEY missing) — falling back to internal proposals only.",
      },
    ];
  }
  return Promise.all(queries.map((q) => tavilySearchOne(q, apiKey)));
}

/**
 * Flatten a list of search results into a context string suitable for
 * passing to the LLM, with deduplicated source URLs so the model can
 * cite them per line item.
 */
export function formatMarketSearchContext(
  results: MarketSearchResult[],
  language: "ar" | "en",
): string {
  const isAr = language === "ar";
  if (!results.length || results.every((r) => r.sources.length === 0)) {
    const w = results.find((r) => r.warning)?.warning;
    return w ? `[${w}]` : "";
  }
  const lines: string[] = [];
  const seen = new Set<string>();
  for (const r of results) {
    if (r.answer) lines.push(`${isAr ? "ملخص" : "Summary"}: ${r.answer}`);
    for (const s of r.sources) {
      if (seen.has(s.url)) continue;
      seen.add(s.url);
      lines.push(`- ${s.title} — ${s.snippet}\n  ${isAr ? "المصدر" : "Source"}: ${s.url}`);
    }
  }
  return lines.join("\n").slice(0, 6000);
}
